import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { academicSessionAPI, classAPI, feeCategoryAPI, feePaymentAPI, studentAPI } from '../utils/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import '../styles/list.css';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const formatDateInput = (date) => date.toISOString().split('T')[0];

const getDefaultDateRange = () => {
  const today = new Date();
  const oneMonthBefore = new Date(today);
  oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);

  return {
    today: formatDateInput(today),
    oneMonthBefore: formatDateInput(oneMonthBefore),
  };
};

const getGenderIconClass = (gender) => {
  if (gender === 'Male') return 'fas fa-mars';
  if (gender === 'Female') return 'fas fa-venus';
  return 'fas fa-user';
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDisplayDate = (value) =>
  new Date(value).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const FeeDetails = () => {
  const navigate = useNavigate();
  const { today, oneMonthBefore } = useMemo(() => getDefaultDateRange(), []);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalAmount: 0,
    averageAmount: 0,
    uniqueStudents: 0,
  });
  const [adminOptions, setAdminOptions] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [classOptions, setClassOptions] = useState([]);
  const [feeCategoryOptions, setFeeCategoryOptions] = useState([]);
  const [academicSessionOptions, setAcademicSessionOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedFeeCategoryId, setSelectedFeeCategoryId] = useState('');
  const [defaultFeeCategoryId, setDefaultFeeCategoryId] = useState('');
  const [selectedAcademicSessionId, setSelectedAcademicSessionId] = useState('');
  const [defaultAcademicSessionId, setDefaultAcademicSessionId] = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [fromDate, setFromDate] = useState(oneMonthBefore);
  const [toDate, setToDate] = useState(today);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [exporting, setExporting] = useState(false);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const reportDayCount = useMemo(() => {
    if (!fromDate || !toDate) {
      return 0;
    }

    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffTime = end.getTime() - start.getTime();

    if (Number.isNaN(diffTime) || diffTime < 0) {
      return 0;
    }

    return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }, [fromDate, toDate]);

  const filterParams = useMemo(() => {
    const trimmedSearch = searchTerm.trim();
    const params = {
      page: currentPage,
      limit: pageSize,
      search: !trimmedSearch || trimmedSearch.length >= MIN_SEARCH_LENGTH ? trimmedSearch : undefined,
      class: selectedClass || undefined,
      feeCategoryId: selectedFeeCategoryId || undefined,
      academicSessionId: selectedAcademicSessionId || undefined,
      adminId: selectedAdminId || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    };

    Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
    return params;
  }, [currentPage, pageSize, searchTerm, selectedClass, selectedFeeCategoryId, selectedAcademicSessionId, selectedAdminId, fromDate, toDate]);

  const summaryParams = useMemo(() => {
    const { page, limit, ...rest } = filterParams;
    return rest;
  }, [filterParams]);

  useEffect(() => {
    fetchClasses();
    fetchFeeCategories();
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() && !selectedStudent && searchTerm.trim().length < MIN_SEARCH_LENGTH) {
      return;
    }
    fetchPageData();
  }, [debouncedSearchTerm, filterParams, summaryParams, selectedStudent]);

  const fetchClasses = async () => {
    try {
      const response = await classAPI.getAll({ includeDisabled: 1 });
      setClassOptions(response.data.data.classes || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchFeeCategories = async () => {
    try {
      const response = await feeCategoryAPI.getAll({ includeDisabled: 1 });
      const categories = response.data.data.feeCategories || [];
      const defaultCategory = categories.find((item) => Number(item.isDefault) === 1);

      setFeeCategoryOptions(categories);
      setDefaultFeeCategoryId(defaultCategory ? String(defaultCategory.id) : '');
      setSelectedFeeCategoryId((prev) => prev || (defaultCategory ? String(defaultCategory.id) : ''));
    } catch (error) {
      console.error('Error fetching fee categories:', error);
    }
  };

  const fetchAcademicSessions = async () => {
    try {
      const response = await academicSessionAPI.getAll({ includeDisabled: 1 });
      const sessions = response.data.data.academicSessions || [];
      const defaultSession = sessions.find((item) => Number(item.isDefault) === 1);

      setAcademicSessionOptions(sessions);
      setDefaultAcademicSessionId(defaultSession ? String(defaultSession.id) : '');
      setSelectedAcademicSessionId((prev) => prev || (defaultSession ? String(defaultSession.id) : ''));
    } catch (error) {
      console.error('Error fetching academic sessions:', error);
    }
  };

  const fetchPageData = async () => {
    setLoading(true);
    setMessage('');
    try {
      const [paymentsResponse, summaryResponse] = await Promise.all([
        feePaymentAPI.getAll(filterParams),
        feePaymentAPI.getSummary(summaryParams),
      ]);

      setPayments(paymentsResponse.data.data.payments || []);
      setTotalPages(paymentsResponse.data.data.pagination?.total_pages || 1);
      setSummary(summaryResponse.data.data.summary || {});
      setAdminOptions(summaryResponse.data.data.admins || []);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.message || 'Failed to load fee details'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchWithDebounce = (value) => {
    if (selectedStudent) {
      return;
    }

    setSearchTerm(value);
    setShowSearchDropdown(value.trim().length >= MIN_SEARCH_LENGTH);
    setCurrentPage(1);
  };

  useEffect(() => {
    const trimmedSearch = debouncedSearchTerm.trim();

    if (selectedStudent) {
      return;
    }

    if (!trimmedSearch) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      return;
    }

    if (trimmedSearch.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setShowSearchDropdown(true);
      setSearchLoading(false);
      return;
    }

    let isActive = true;

    const searchStudents = async () => {
      setSearchLoading(true);

      try {
        const response = await studentAPI.getAll({
          search: trimmedSearch,
          limit: 10,
        });
        if (isActive) {
          setSearchResults(response.data.data.students || []);
        }
      } catch (error) {
        if (isActive) {
          setSearchResults([]);
        }
      } finally {
        if (isActive) {
          setSearchLoading(false);
        }
      }
    };

    searchStudents();

    return () => {
      isActive = false;
    };
  }, [debouncedSearchTerm, selectedStudent]);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedStudent(null);
    setSelectedClass('');
    setSelectedFeeCategoryId(defaultFeeCategoryId);
    setSelectedAcademicSessionId(defaultAcademicSessionId);
    setSelectedAdminId('');
    setFromDate(oneMonthBefore);
    setToDate(today);
    setCurrentPage(1);
    setSearchResults([]);
    setShowSearchDropdown(false);
  };

  const handleViewDetails = async (paymentId) => {
    try {
      const response = await feePaymentAPI.getById(paymentId);
      setSelectedPayment(response.data.data.payment);
      setShowDetails(true);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.message || 'Failed to fetch payment details'}`);
    }
  };

  const handlePrintReceipt = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleExport = async () => {
    setExporting(true);
    setMessage('');

    try {
      const response = await feePaymentAPI.export(summaryParams);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

      link.href = blobUrl;
      link.setAttribute('download', `fee-report-${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.message || 'Failed to export fee data'}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="list-page">
        <div className="list-header">
          <h1>Fee Details</h1>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => navigate('/fee-payment')}>
              <i className="fas fa-plus"></i> Add New Payment
            </button>
          </div>
        </div>

        {message && (
          <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
            {message}
          </div>
        )}

        <div className="card fee-filter-card">
          <div className="filter-header">
            <h3><i className="fas fa-filter"></i> Detailed Fee Filters</h3>
          </div>

          <div className="filter-content">
            <div className="filter-row">
              <div className="filter-group autocomplete-group">
                <label>Search Student</label>
                <div className="autocomplete-wrapper">
                  <input
                    type="text"
                    placeholder="Type at least 3 characters..."
                    value={searchTerm}
                    onChange={(e) => handleSearchWithDebounce(e.target.value)}
                    onFocus={() => {
                      if (searchTerm.trim().length >= MIN_SEARCH_LENGTH && !selectedStudent) {
                        setShowSearchDropdown(true);
                      }
                    }}
                    className="filter-input"
                    autoComplete="off"
                    disabled={selectedStudent !== null}
                  />
                  {selectedStudent && (
                    <button
                      className="btn-clear-student"
                      onClick={() => {
                        setSelectedStudent(null);
                        setSearchTerm('');
                        setSearchResults([]);
                        setShowSearchDropdown(false);
                        setCurrentPage(1);
                      }}
                      title="Clear student selection"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                  {showSearchDropdown && !selectedStudent && (
                    <div className="autocomplete-dropdown">
                      {searchTerm.trim() && searchTerm.trim().length < MIN_SEARCH_LENGTH ? (
                        <div className="autocomplete-empty">Type at least 3 characters to search</div>
                      ) : searchLoading ? (
                        <div className="autocomplete-loading">
                          <i className="fas fa-spinner fa-spin"></i> Searching...
                        </div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((student) => (
                          <div
                            key={student.id}
                            className="autocomplete-item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSelectedStudent(student);
                              setSearchTerm(student.name);
                              setShowSearchDropdown(false);
                              setSearchResults([]);
                              setCurrentPage(1);
                            }}
                          >
                            <strong className="name-with-gender">
                              <i
                                className={`${getGenderIconClass(student.gender)} gender-icon gender-icon-${(student.gender || '').toLowerCase()}`}
                                aria-hidden="true"
                              ></i>
                              <span>{student.name}</span>
                            </strong>
                            <span className="class-badge">Class {student.class}</span>
                          </div>
                        ))
                      ) : searchTerm.trim() ? (
                        <div className="autocomplete-empty">No students found</div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              <div className="filter-group">
                <label>Class</label>
                <select
                  value={selectedClass}
                  onChange={(e) => {
                    setSelectedClass(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-input"
                >
                  <option value="">All Classes</option>
                  {classOptions.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Fee Category</label>
                <select
                  value={selectedFeeCategoryId}
                  onChange={(e) => {
                    setSelectedFeeCategoryId(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-input"
                >
                  <option value="">All Fee Categories</option>
                  {feeCategoryOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                      {Number(item.isDefault) === 1 ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Academic Session</label>
                <select
                  value={selectedAcademicSessionId}
                  onChange={(e) => {
                    setSelectedAcademicSessionId(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-input"
                >
                  <option value="">All Academic Sessions</option>
                  {academicSessionOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                      {Number(item.isDefault) === 1 ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Received By</label>
                <select
                  value={selectedAdminId}
                  onChange={(e) => {
                    setSelectedAdminId(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-input"
                >
                  <option value="">All Admins</option>
                  {adminOptions.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  max={today}
                  className="filter-input"
                />
              </div>

              <div className="filter-group">
                <label>To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  max={today}
                  className="filter-input"
                />
              </div>

              <div className="filter-group filter-actions">
                <button className="btn btn-secondary filter-btn min-height-46" onClick={resetFilters}>
                  <i className="fas fa-rotate-right"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="section-header">
          <h2>
            Filtered Fee Report  <small className="text-muted">From {formatDisplayDate(fromDate)} To {formatDisplayDate(toDate)} ({reportDayCount} {reportDayCount === 1 ? 'Day' : 'Days'})</small>
          </h2>
        </div>

        <div className="fee-summary-grid">
          <div className="stat-card fee-stat-card">
            <div className="stat-icon">
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="stat-content">
              <h3>Total Collection</h3>
              <p className="stat-number">{formatCurrency(summary.totalAmount)}</p>
            </div>
          </div>
          <div className="stat-card fee-stat-card">
            <div className="stat-icon">
              <i className="fas fa-receipt"></i>
            </div>
            <div className="stat-content">
              <h3>Total Payments</h3>
              <p className="stat-number">{summary.totalPayments || 0}</p>
            </div>
          </div>
          {/* <div className="stat-card fee-stat-card">
            <div className="stat-content">
              <h3>Average Payment</h3>
              <p className="stat-number">{formatCurrency(summary.averageAmount)}</p>
            </div>
          </div> */}
          <div className="stat-card fee-stat-card">
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-content">
              <h3>Students Covered</h3>
              <p className="stat-number">{summary.uniqueStudents || 0}</p>
            </div>
          </div>
        </div>

        <div className="fee-details-grid">
          <div className="card">
            <div className="card-header">
              <div className="list-header mb-0">
                <h3 className="card-title">Filtered Fee Entries</h3>
                <button className="btn btn-secondary" onClick={handleExport} 
                disabled={exporting || loading || payments.length === 0}>
                  <i className="fas fa-file-csv"></i> {exporting ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>

            {loading && payments.length === 0 ? (
              <div className="spinner"></div>
            ) : payments.length === 0 ? (
              <div className="empty-state">
                <p>No fee entries found for these filters.</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Class</th>
                        <th>Mobile No</th>
                        <th>Session</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <button
                              type="button"
                              className="btn-link student-name-link"
                              onClick={() => navigate(`/students/${payment.student_id}`)}
                            >
                              <i
                                className={`${getGenderIconClass(payment.gender)} gender-icon gender-icon-${(payment.gender || '').toLowerCase()}`}
                                aria-hidden="true"
                              ></i>
                              <span>{payment.student_name}</span>
                            </button>
                          </td>
                          <td>{payment.class}</td>
                          <td>{payment.mobileNumber}</td>
                          <td>{payment.academic_session_name || '-'}</td>
                          <td>{payment.fee_category_name || '-'}</td>
                          <td>{formatCurrency(payment.amount)}</td>
                          <td>{new Date(payment.payment_date).toLocaleDateString('en-IN')}</td>
                          <td className='width-120'>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => handleViewDetails(payment.id)}
                            >
                              <i className="fas fa-eye"></i> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="fee-pagination-section">
                  <div className="pagination-left">
                    <label className="rows-per-page">
                      Rows per page:
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(parseInt(e.target.value, 10));
                          setCurrentPage(1);
                        }}
                        className="page-size-select"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </label>
                    <span className="page-info">Total: {summary.totalPayments || 0} filtered payments</span>
                  </div>

                  <div className="pagination-center">
                    <span className="page-indicator">
                      Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                    </span>
                  </div>

                  <div className="pagination-controls">
                    <button
                      className="btn btn-pagination"
                      onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-chevron-left"></i> Previous
                    </button>
                    <button
                      className="btn btn-pagination"
                      onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

        </div>

        {showDetails && selectedPayment && (
          <div className="modal-overlay" onClick={() => setShowDetails(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Payment Details</h2>
                <button className="modal-close" onClick={() => setShowDetails(false)}>
                  <i className="fas fa-xmark"></i>
                </button>
              </div>

              <div className="modal-body">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Student Name:</label>
                    <p className="name-with-gender">
                      <i
                        className={`${getGenderIconClass(selectedPayment.gender)} gender-icon gender-icon-${(selectedPayment.gender || '').toLowerCase()}`}
                        aria-hidden="true"
                      ></i>
                      <span>{selectedPayment.student_name}</span>
                    </p>
                  </div>
                  <div className="detail-item">
                    <label>Father's Name:</label>
                    <p>{selectedPayment.fatherName}</p>
                  </div>
                  <div className="detail-item">
                    <label>Class:</label>
                    <p>{selectedPayment.class}</p>
                  </div>
                  <div className="detail-item">
                    <label>Amount:</label>
                    <p>{formatCurrency(selectedPayment.amount)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Academic Session:</label>
                    <p>{selectedPayment.academic_session_name || '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Fee Category:</label>
                    <p>{selectedPayment.fee_category_name || '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Payment Date:</label>
                    <p>{new Date(selectedPayment.payment_date).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div className="detail-item">
                    <label>Payment Received By:</label>
                    <p>{selectedPayment.admin_name}</p>
                  </div>
                  {selectedPayment.remark && (
                    <div className="detail-item detail-item-wide">
                      <label>Remark:</label>
                      <p>{selectedPayment.remark}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={handlePrintReceipt}
                >
                  <i className="fas fa-print"></i> Print Receipt
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {selectedPayment && (
        <div className="receipt-container">
          <div className="receipt">
            <div className="receipt-header">
              <p className="receipt-kicker">Fee Payment Receipt</p>
              <h1 className="receipt-title">ABC School & College</h1>
              <p className="receipt-subtitle">School Road, City</p>
              <p className="receipt-subtitle">Contact: (123) 456-7890</p>
            </div>

            <div className="receipt-body">
              <div className="receipt-meta">
                <div className="receipt-meta-row">
                  <span>Receipt No</span>
                  <strong>RCP{String(selectedPayment.id).padStart(4, '0')}</strong>
                </div>
                <div className="receipt-meta-row">
                  <span>Payment Date</span>
                  <strong>{new Date(selectedPayment.payment_date).toLocaleDateString('en-IN')}</strong>
                </div>
                <div className="receipt-meta-row">
                  <span>Printed</span>
                  <strong>{new Date().toLocaleString('en-IN')}</strong>
                </div>
              </div>

              <div className="receipt-line-divider"></div>

              <div className="receipt-section">
                <h3>Student Details</h3>
                <div className="receipt-detail-list">
                  <div className="receipt-detail-row">
                    <span>Student</span>
                    <strong>{selectedPayment.student_name}</strong>
                  </div>
                  <div className="receipt-detail-row">
                    <span>Father</span>
                    <strong>{selectedPayment.fatherName}</strong>
                  </div>
                  <div className="receipt-detail-row">
                    <span>Class</span>
                    <strong>{selectedPayment.class}</strong>
                  </div>
                </div>
              </div>

              <div className="receipt-line-divider"></div>

              <div className="receipt-section">
                <h3>Payment Details</h3>
                <div className="receipt-amount-panel">
                  <span>Amount Paid</span>
                  <p className="amount">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div className="receipt-detail-list">
                  <div className="receipt-detail-row">
                    <span>Academic Session</span>
                    <strong>{selectedPayment.academic_session_name || '-'}</strong>
                  </div>
                  <div className="receipt-detail-row">
                    <span>Fee Category</span>
                    <strong>{selectedPayment.fee_category_name || '-'}</strong>
                  </div>
                  <div className="receipt-detail-row">
                    <span>Received By</span>
                    <strong>{selectedPayment.admin_name}</strong>
                  </div>
                  {selectedPayment.remark && (
                    <div className="receipt-detail-row receipt-detail-row-stack">
                      <span>Remark</span>
                      <strong>{selectedPayment.remark}</strong>
                    </div>
                  )}
                </div>
              </div>

              <div className="receipt-line-divider"></div>

              <div className="receipt-section receipt-signoff">
                <p>This is a system-generated receipt.</p>
                <p>Please keep this slip for your records.</p>
              </div>
            </div>

            <div className="receipt-footer">
              <div className="receipt-bottom-line"></div>
              <p className="receipt-thank-you">Thank you for your payment</p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FeeDetails;
